import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type HttpError } from '@atlaskit/teams-client';
import type { ExperienceEvent } from '@atlassian/experience-tracker/ExperienceEvent';
import { ExperienceTracker } from '@atlassian/experience-tracker/ExperienceTracker';
import {
	AppNames,
	BROWSE_USER_SETTINGS_KEY,
	getAppName,
} from '@atlassian/teams-common/context-config';

import {
	type Analytics,
	AnalyticsEventSource,
	type EventAttributes,
} from '../components/AnalyticsContext';
import { type FeatureFlag, isFlagEnabled } from '../modules/featureFlags';
import { ignoredErrorReason, isAuthError, isErrorStatusCode, StatusCode } from '../utils/error';

export function configureNewTracker(
	analytics: Analytics,
	analyticsNext: AnalyticsWebClient,
): ExperienceTracker {
	const tracker = new DirectoryExperienceTracker({
		isNetworkOfflineError: isNetworkError,
	});

	tracker.subscribe((event) => {
		if (
			event.action === 'taskStart' ||
			event.action === 'taskSuccess' ||
			event.action === 'taskAbort' ||
			event.action === 'taskFail'
		) {
			const performanceAttributes = computePerformanceAttributes(event);
			const errorAttributes = computeErrorAttributes(event);
			const finalErrorDetails = Object.assign(
				errorAttributes,
				performanceAttributes,
				event.attributes,
				{
					// Because standalone app is deployed to prod quickly,
					// we will use this attribute to query in Splunk sooner.
					isStandalone: getAppName() === AppNames.STANDALONE_DIRECTORY,
					hasBrowseUsersPermission: isFlagEnabled(BROWSE_USER_SETTINGS_KEY as FeatureFlag),
				},
			);
			const actionSubject = sanitiseExperienceName(event.name);

			if (fg('ptc-enable-ptc-common-analytics-refactor')) {
				analyticsNext.sendOperationalEvent({
					source: AnalyticsEventSource.CLIENT_METRICS,
					actionSubject,
					action: event.action,
					attributes: finalErrorDetails,
				});
			} else {
				analytics.pushOperationalEvent({
					source: AnalyticsEventSource.CLIENT_METRICS,
					actionSubject,
					action: event.action,
					attributes: finalErrorDetails,
				});
			}
		}
	});

	// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
	window.addEventListener('unload', () => {
		tracker.abort({
			reason: 'Abort due to Window unloading',
			attributes: {
				cause: 'WINDOW_UNLOAD',
			},
		});
	});

	// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
	window.addEventListener('pagehide', () => {
		tracker.abort({
			reason: 'Window unloading',
			attributes: {
				cause: 'PAGE_HIDE',
			},
		});
	});

	return tracker;
}

function sanitiseExperienceName(name: string) {
	return name.replace(/[\/-]/g, '_');
}

function computeErrorAttributes(event: ExperienceEvent): EventAttributes {
	const attributes: Record<string, string | null> = {};
	if (event.action === 'taskFail' && event.error) {
		attributes.failError = event.error.message;
		attributes.traceId = (event.error as HttpError).traceId ?? null;
	}

	if (event.action === 'taskAbort' && event.reason) {
		attributes.abortReason = event.reason;
	}

	return attributes;
}

// compute sloSatisfied: true/false based on the latencySlo attribute
// for monitoring in SignalFx
function computePerformanceAttributes(event: ExperienceEvent) {
	if (event.action === 'taskStart') {
		return {};
	}
	const duration = event.duration;
	const latencySlo = event.attributes && (event.attributes as any)['latencySlo'];
	if (typeof duration !== 'number' || typeof latencySlo !== 'number') {
		return {};
	}

	return {
		sloSatisfied: duration <= latencySlo,
		taskDuration: duration,
		latencySlo,
	};
}

type OptionsType = ConstructorParameters<typeof ExperienceTracker>[0];

class DirectoryExperienceTracker extends ExperienceTracker {
	constructor(options: OptionsType) {
		super(options);
	}

	stopOnError({ name, error, attributes }: { name: string; error: Error; attributes: object }) {
		if (isAuthError(error)) {
			this.abort({
				name,
				reason: 'Abort due to user unauthenticated',
				attributes: { cause: 'USER_UNAUTHENTICATED', error, ...attributes },
				checkForTimeout: false,
			});
			return;
		}

		if (isErrorStatusCode(StatusCode.GONE, error)) {
			this.abort({
				name,
				reason: 'Abort due to unavailable resource',
				attributes: { cause: 'GONE', error, ...attributes },
				checkForTimeout: false,
			});
			return;
		}

		const reason = ignoredErrorReason(error);
		if (reason) {
			this.abort({
				name,
				reason: 'Abort due to an error in ignore list',
				attributes: {
					cause: reason,
					...attributes,
				},
				checkForTimeout: false,
			});
			return;
		}

		super.stopOnError({ name, error, attributes });
	}
}

export function isNetworkError(error: Error): boolean {
	return (
		error &&
		!!error.message &&
		// Firefox
		(error.message.includes('NetworkError') ||
			// Chrome
			error.message.startsWith('Failed to fetch') ||
			error.message.includes('GraphQL error: Failed to fetch') ||
			// cancel by users or unknown reason
			error.message.includes('GraphQL error: The operation was aborted') ||
			error.message.includes('GraphQL error: cancelled') ||
			error.message.includes('Abgebrochen') ||
			error.message.includes('отменено') ||
			error.message.includes('Сетевое соединение потеряно.') ||
			error.message.includes('anulowane') ||
			error.message.includes('annulé') ||
			error.message.includes('已取消') ||
			error.message.includes('cancelado') ||
			error.message.includes('キャンセルしました') ||
			error.message.includes('cancelled') ||
			error.message.includes('Network error') ||
			// GraphQL-wrapping network error
			// @ts-ignore
			!!(error['graphQLErrors'] && error['networkError']))
	);
}
